import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import batteryPackService from '../../services/batteryPackService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Skeleton,
  Avatar,
  Stack,
  Button as MuiButton,
} from '@mui/material';
import {
  BatteryChargingFull as BatteryIcon,
  Refresh as RefreshIcon,
  PowerSettingsNew as PowerIcon,
  Speed as SpeedIcon,
  LocationOn as MapPinIcon,
  Schedule as ClockIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

const BatteryPackPage = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDevices: 0,
    activeDevices: 0,
    avgVoltage: 0,
    criticalDevices: 0
  });

  useEffect(() => {
    fetchBatteryPacks();
  }, []);

  const fetchBatteryPacks = async () => {
    try {
      setLoading(true);
      const statsRes = await batteryPackService.getBatteryStats();
      const devicesRes = await batteryPackService.getBatteryDevices();
      setDevices(devicesRes.data || devicesRes || []);
      const batteryStats = statsRes.data || statsRes || {};
      setStats({
        totalDevices: batteryStats.total || 0,
        activeDevices: batteryStats.active || 0,
        avgVoltage: batteryStats.avgVoltage || 0,
        criticalDevices: batteryStats.critical || 0
      });
    } catch (err) {
      console.error('Failed to fetch battery packs', err);
    } finally {
      setLoading(false);
    }
  };

  const getVoltageColor = (voltage) => {
    if (voltage >= 12.5) return '#10b981';
    if (voltage >= 11.5) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <Box p={3} className="animate-fade-in">
      {/* Actions — no page title, the top bar already names the page */}
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <MuiButton variant="outlined" startIcon={<RefreshIcon />} onClick={fetchBatteryPacks} sx={{ borderRadius: '14px', px: 4, fontWeight: 700 }}>Telemetry Sync</MuiButton>
      </Box>

      <Grid container spacing={3} mb={5}>
        {[
          { label: 'Total Packs', value: stats.totalDevices, icon: <BatteryIcon />, color: '#3b82f6' },
          { label: 'Units Active', value: stats.activeDevices, icon: <PowerIcon />, color: '#10b981' },
          { label: 'Avg Voltage', value: `${stats.avgVoltage}V`, icon: <SpeedIcon />, color: '#8b5cf6' },
          { label: 'Critical Alert', value: stats.criticalDevices, icon: <WarningIcon />, color: '#ef4444' }
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, borderRadius: '12px' }}>{stat.icon}</Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{loading ? <Skeleton width={40} /> : stat.value}</Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} mb={5}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 350 }}>
            <Box p={2.5} sx={{ borderBottom: 1, borderColor: 'divider' }}><Typography variant="h6" fontWeight={800}>State of Health</Typography></Box>
            <CardContent sx={{ height: 260, display: 'flex', alignItems: 'center' }}>
              {loading ? <Skeleton width="100%" height="80%" /> : (
                <Doughnut
                  data={{
                    labels: ['Nominal', 'Low', 'Critical'],
                    datasets: [{ data: [devices.length - stats.criticalDevices, stats.criticalDevices * 0.7, stats.criticalDevices * 0.3], backgroundColor: ['#10b981', '#f59e0b', '#ef4444'], borderWidth: 0, cutout: '70%' }]
                  }}
                  options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 20, font: { weight: 'bold' } } } } }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: 350 }}>
            <Box p={2.5} sx={{ borderBottom: 1, borderColor: 'divider' }}><Typography variant="h6" fontWeight={800}>Voltage Distribution Map</Typography></Box>
            <CardContent sx={{ height: 260 }}>
              {loading ? <Skeleton width="100%" height="100%" /> : (
                <Bar
                  data={{
                    labels: ['10V-11V', '11V-12V', '12V-13V', '13V+'],
                    datasets: [{ label: 'Battery Packs', data: [stats.criticalDevices, 5, 12, 3], backgroundColor: '#3b82f6', borderRadius: 8 }]
                  }}
                  options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false } }, y: { beginAtZero: true } } }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {loading ? [...Array(6)].map((_, i) => (
          <Grid item xs={12} md={4} key={i}><Skeleton variant="rectangular" height={220} sx={{ borderRadius: 4 }} /></Grid>
        )) : devices.map(device => {
          const voltage = parseFloat(device.battery_monitor) || 0;
          const batteryPercent = Math.min(100, Math.max(0, ((voltage - 10.5) / 2.5) * 100));
          return (
            <Grid item xs={12} sm={6} md={4} key={device.device_id}>
              <Card
                onClick={() => navigate(`/devices/${device.device_id}`)}
                sx={{ cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-6px)', boxShadow: 'var(--shadow-xl)' }, borderTop: `4px solid ${getVoltageColor(voltage)}` }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" mb={3}>
                    <Typography variant="h6" fontWeight={800}>{device.device_id}</Typography>
                    <Chip label={device.status} size="small" variant="outlined" sx={{ fontWeight: 800, borderRadius: '6px' }} />
                  </Box>
                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" fontWeight={800} color="text.secondary">POTENTIAL DIFFERENCE</Typography>
                      <Typography variant="subtitle2" fontWeight={800} color={getVoltageColor(voltage)}>{voltage.toFixed(2)}V</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={batteryPercent} sx={{ height: 10, borderRadius: 5, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { bgcolor: getVoltageColor(voltage), borderRadius: 5 } }} />
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'right', fontWeight: 700, opacity: 0.6 }}>Approx. {batteryPercent.toFixed(0)}% SOH</Typography>
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                      <MapPinIcon sx={{ fontSize: 16 }} /><Typography variant="caption" fontWeight={600}>Grid-A Sector 4</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                      <ClockIcon sx={{ fontSize: 16 }} /><Typography variant="caption" fontWeight={600}>{new Date(device.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default BatteryPackPage;
